/**
 * Non-authoritative navigation index for ResponsibilityAssignments
 * (ADR-DEV-DDX-UI-002). Backend has assign/revoke but no list Capability.
 * Business Guard remains server-side; this only enables Team/§8 UI reflection
 * for assignments discovered in-session via assign responses.
 */

import type { ResponsibilityAssignment } from "../api/types";

const KEY = "ddx.knownAssignments";

type Index = Record<string, ResponsibilityAssignment[]>;

function readIndex(): Index {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Index;
  } catch {
    return {};
  }
}

function writeIndex(index: Index): void {
  sessionStorage.setItem(KEY, JSON.stringify(index));
}

export function listKnownAssignments(
  projectId: string,
): ResponsibilityAssignment[] {
  const list = readIndex()[projectId] ?? [];
  return Array.isArray(list) ? list : [];
}

export function rememberAssignment(
  assignment: ResponsibilityAssignment,
): void {
  const projectId = assignment.project_id;
  if (!projectId || !assignment.assignment_id) return;
  const index = readIndex();
  const prev = index[projectId] ?? [];
  const next = [
    assignment,
    ...prev.filter((a) => a.assignment_id !== assignment.assignment_id),
  ].slice(0, 200);
  index[projectId] = next;
  writeIndex(index);
}

export function forgetAssignment(
  projectId: string,
  assignmentId: string,
): void {
  const index = readIndex();
  index[projectId] = (index[projectId] ?? []).filter(
    (a) => a.assignment_id !== assignmentId,
  );
  writeIndex(index);
}
